import { Injectable } from '@angular/core';
import { AppConfig } from './app.config';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SignalRService } from './services/SignalRService';
import { Property } from './models/property';
import { AuthenticationService } from './services/AuthenticationService';
import { ApplicationUser } from './models/applicationUser';
import { PropertyService } from './services/PropertyService';
import { GeneralDataService } from './services/GeneralDataService';
import { AuctionService } from './services/AuctionService';
import { PropertyType } from './models/propertyType';
import { State } from './models/state';
import { SaleType } from './models/saleType';
import { PropertyFeatures } from './models/propertyFeatures';
import { Auction } from './models/auction';
import { AuctionType } from './models/auctionType';
import { UserType } from './models/userType';
import { AuctionAdminDefaults } from './models/auctionAdminDefaults';
import { SearchItem, SearchResult } from './models/searchResult';
import { LocationService } from './services/LocationService';
import { PropertyFilterParameters } from './models/propertyFilterParameters';
import { InventoryStatus } from './models/inventoryStatus';
import { DocumentVisibility } from './models/documentVisibility';
import { PropertyPublishingData } from './models/propertyPublishingData';
import { PropertyLockPublishingData } from './models/propertyLockPublishingData';
import { PropertyAbstract } from './models/propertyAbstract';
import { PropertyImage } from './models/propertyImage';
import { PortfolioService } from './services/PortfolioService';
import { CheckboxOption } from './theme/interfaces/checkboxOption';
import { SoldProperty } from './models/soldProperty';
import { PropertyPhasePublishingData } from './models/propertyPhasePublishingData';


@Injectable({
  providedIn: 'root'
})
export class AppService {
  public clientSessionId: string = crypto.randomUUID();

  static timeDiff:number = 0; //timediff in milliseconds from server
  static timeSync(time:Date){
    AppService.timeDiff = time.getTime()-Date.now();
  }
  
  static get correctedDateNowUnix():number{
    return Date.now()+AppService.timeDiff;
  }

  private auctionAdminDefaultsCache?:AuctionAdminDefaults;
  private auctionAdminDefaultsObservableCache?:Observable<AuctionAdminDefaults>;
  private _presetPropertyFilterParameters:PropertyFilterParameters = new PropertyFilterParameters();

  public userTypes:UserType[] = [
    { id: 1, name: 'Private Buyer (Create an account for yourself)' },
    { id: 2, name: 'Corporate Buyer (Create an account for your company)' },
    { id: 3, name: 'Agent / Broker' }
  ];

  public auctionTypes:AuctionType[] = [
    { id: 1, name: 'Auction' },
    { id: 2, name: 'Make Offer/Buy Now' },
    { id: 3, name: 'Traditional Sale' },
    { id: 4, name: 'Call For Offers' }
  ];

  public defaultCheckboxOptions: CheckboxOption[] = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No', matchesUndefined: true},
  ];

  public inventoryStatusList:InventoryStatus[] = [
    {id:0,name:"N/A"},
    {id:10,name:"Assigned"},
    {id:20,name:"Probate"},
    {id:30, name:"Active"}
  ]; //need to come up with a realistic status list from Jeff

  //stable objects (selects compare them by identity); the internal entry is named after the business (appConfig.businessNameLabel)
  public readonly documentVisibilityList:DocumentVisibility[] = this.createDocumentVisibilityList();

  private createDocumentVisibilityList():DocumentVisibility[]{
    const service = this;
    return [
      { id: 0, get name(){ return `${service.appConfig.businessNameLabel} Internal`.trim(); } },
      { id: 2, name: 'Public NDA Required' },
      { id: 9, name: 'Public Visible' }
    ];
  }

  public inventoryToProperty?:Property;
  public inventoryFromProperty?:number;

  public setPresetPropertyFilterParameters(value:PropertyFilterParameters|undefined,triggerEvent:boolean){
    this._presetPropertyFilterParameters=value?value:new PropertyFilterParameters();
    if(triggerEvent) this.presetPropertySearchSubject.next(this._presetPropertyFilterParameters);
  }

  public get presetPropertyFilterParameters(){
    return this._presetPropertyFilterParameters;
  }

  public filteredPropertyIdList?:number[];
  public dashboardFilterParams?:PropertyFilterParameters;
  public dashboardScrollPosition:number=0;

  public propertyUpdateSubject:Subject<Property|undefined> = new Subject();
  public propertyDeleteSubject:Subject<number> = new Subject();
  public presetPropertySearchSubject:Subject<PropertyFilterParameters> = new Subject();
  public propertyLockSubject:Subject<PropertyLockPublishingData> = new Subject();
  public propertImageFullScreenSubject:Subject<PropertyImage[]> = new Subject();
  public propertyCacheUpdatedSubject:Subject<void> = new Subject();

  constructor(public httpClient:HttpClient, private signalRService:SignalRService, 
              private generalDataService:GeneralDataService, private authService:AuthenticationService, private propertyService:PropertyService, private portfolioService:PortfolioService,
              private auctionService:AuctionService, private locationService:LocationService, private appConfig:AppConfig) {
  }

  private initialized = false;

  public init(){
    if(this.initialized) return;
    this.initialized = true;
    this.authService.authenticatedSubject.subscribe((value)=>{
      this.processUserAuthentication(value);
    });
    this.propertyService.propertyCacheUpdatedSubject.subscribe((value)=>{
      this.propertyCacheUpdatedSubject.next();
    });
    this.propertyService.propertyUpdateSubject.subscribe((value)=>{
      this.propertyUpdateSubject.next(value);
    });
    this.signalRService.newHubMessageReceivedSubject.subscribe(data=>{
      this.processNewHubMessage(data);
    });
    this.signalRService.connectionReconnectedSubject.subscribe(()=>{
      this.processHubReconnection();
    });
    this.authService.init();
    this.locationService.getCurrentLocation().then((location:any)=>{
      if(location){
        /*
        this.getAddress(location.lat,location.lng).then((result:any)=>{
          //we will use that later in the searchbox, but need to define precision of and radius around the location first.
        });
        */
      };
    });
  }

  get authorized(){
    return this.authService.authorizedSubject.value;
  }
 
  get authenticated(){
    return this.authService.currentUserValue?true:false;
  }

  get currentUser(){
    return this.authService.currentUserValue;
  }

  get connectionId(){
    return this.signalRService.connectionId;
  }

  get currentUserIsSuperAdmin(){
    return this.authService.currentUserValue?.role=='SuperAdmin';
  }

  get currentUserIsClientUser(){
    return this.authService.currentUserValue?.role=='Client';
  }

  get currentUserIsAttorneyUser(){
    return this.authService.currentUserValue?.role=='Attorney';
  }

  get currentUserIsViewerUser(){
    return this.authService.currentUserValue?.role=='Viewer';
  }

  get auctionAdminDefaults() {
    // Data available
    if(this.auctionAdminDefaultsCache) return of(this.auctionAdminDefaultsCache);
    // Request pending
    else if (this.auctionAdminDefaultsObservableCache) return this.auctionAdminDefaultsObservableCache;
    // New request needed
    else return this.auctionAdminDefaultsObservableCache=this.fetchAuctionAdminDefaults();
  }

  private fetchAuctionAdminDefaults(){
    return this.propertyService.getAuctionAdminDefaults().pipe(map((aad:AuctionAdminDefaults)=> this.processAuctionAdminDefaults(aad)));
  }

  private processAuctionAdminDefaults(aad:AuctionAdminDefaults){
    this.auctionAdminDefaultsObservableCache=undefined;
    this.auctionAdminDefaultsCache=aad;
    return this.auctionAdminDefaultsCache;
  }

  private processUserAuthentication(au:ApplicationUser | undefined){
    this.signalRService.authenticateSubscriber();
  }

  private processNewHubMessage(data:any){
    switch(data.type){
      case "PropertyLock":{
        const plpd = data.obj as PropertyLockPublishingData;
        //only trigger event if this message is not caused by this user connection
        if(plpd.clientSessionId!=this.clientSessionId) this.propertyLockSubject.next(plpd);
        break;
      }
      case "PropertyUpdate":{
        //get full property update from server
        const ppd = data.obj as PropertyPublishingData;
        this.propertyService.getPropertyById(ppd.propertyId, true).pipe(map((p=>{
          this.propertyUpdateSubject.next(p);
        })),take(1)).subscribe();
        break;
      }
      case "PropertyPhase":{
        const pppd = data.obj as PropertyPhasePublishingData;
        this.propertyService.setPropertyPhase(pppd.propertyId, pppd.propertyPhaseId);
        break;
      }
      case "PropertyDelete":{
        const ppd = data.obj as PropertyPublishingData;
        this.propertyService.processRemoveProperty(ppd.propertyId, ppd.physicallyDeleted);
        this.propertyDeleteSubject.next(ppd.propertyId);
        break;
      }
      case "PropertyViewed":
        this.propertyService.processPropertyViews(data.obj);
        break;
      case "Bid":{
        this.propertyService.getPropertyById(data.obj.propertyId, false).pipe(map((p=>{
          if(p){
            p.updated=data.obj.updated;
            this.auctionService.processBidPublishingData(data.obj, p);
            this.propertyService.updateLatestPropertyUpdatedTimeStamp(p);
          }
        })),take(1)).subscribe();
        break;
      }
      case "BidEqualized":
        this.auctionService.processBidEqualizedPublishingData(data.obj);
        break;
      case "AuctionStatus":{
        this.propertyService.getPropertyById(data.obj.propertyId, false).pipe(map((p=>{
          if(p){
            p.updated=data.obj.updated;
            this.auctionService.processAuctionStatus(data.obj, p);
            this.propertyService.updateLatestPropertyUpdatedTimeStamp(p);
          }
        })),take(1)).subscribe();
        break;
      }
      case "AuctionStarted":{
        //force refresh as schedule might have been advanced
        this.propertyService.getPropertyById(data.obj.propertyId, true).pipe(map((p=>{
          if(p){
            this.propertyUpdateSubject.next(p);
            this.propertyService.updateLatestPropertyUpdatedTimeStamp(p);
          }
        })),take(1)).subscribe();
        break;
      }
      case "AuctionEnded":{
        //force refresh as schedule might have been advanced
        this.propertyService.getPropertyById(data.obj.propertyId, true).pipe(map((p=>{
          if(p){
            this.propertyUpdateSubject.next(p);
            this.propertyService.updateLatestPropertyUpdatedTimeStamp(p);
          }
        })),take(1)).subscribe();
        break;
      }
      case "AuctionCancelled":{
        this.propertyService.getPropertyById(data.obj.propertyId, true).pipe(map((p=>{
          if(p){
            p.updated=data.obj.updated;
            this.auctionService.processAuctionStatus(data.obj, p);
            this.propertyService.updateLatestPropertyUpdatedTimeStamp(p);
          }
        })),take(1)).subscribe();
        break;
      }
      case "AuctionEndPostponed":{
        this.propertyService.getPropertyById(data.obj.propertyId, false).pipe(map((p=>{
          if(p){
            p.updated=data.obj.updated;
            this.auctionService.processAuctionEndPostponedPublishingData(data.obj, p);
            this.propertyService.updateLatestPropertyUpdatedTimeStamp(p);
          }
        })),take(1)).subscribe();
        break;
      }
      case "State":
        this.generalDataService.processStateUpdate(data.obj);
        break;
      case "County":
        this.generalDataService.processCountyUpdate(data.obj);
        break;
      case "LegalAdRule":
        this.generalDataService.processLegalAdRuleUpdate(data.obj);
        break;
      case "DeleteLegalAdRule":
        this.generalDataService.processLegalAdRuleDelete(data.obj);
        break;
      case "AuctionRule":
        this.generalDataService.processAuctionRuleUpdate(data.obj);
        break;
      case "DeleteAuctionRule":
        this.generalDataService.processAuctionRuleDelete(data.obj);
        break;
      case "LegalInformationRule":
        this.generalDataService.processLegalInformationRuleUpdate(data.obj);
        break;
      case "DeleteLegalInformationRule":
        this.generalDataService.processLegalInformationRuleDelete(data.obj);
        break;
      case "FolderStructureTemplate":
        this.generalDataService.processFolderStructureTemplateUpdate(data.obj);
        break;
      case "DeleteFolderStructureTemplate":
        this.generalDataService.processFolderStructureTemplateDelete(data.obj);
        break;
      case "DocumentTemplate":
        this.generalDataService.processDocumentTemplateUpdate(data.obj);
        break;
      case "DeleteDocumentTemplate":
        this.generalDataService.processDocumentTemplateDelete(data.obj);
        break;
      case "EmailTemplate":
        this.generalDataService.processEmailTemplateUpdate(data.obj);
        break;
      case "PrivateAuctionAuthorization":{
        //triggering a property update will also pull the authorizations from scratch and therefor update them for this specific user
        this.propertyService.getPropertyById(data.obj, true).pipe(map((p=>{
          this.propertyUpdateSubject.next(p);
        })),take(1)).subscribe();
        break;
      }
      case "LockAccount":
        this.authService.lockAccount(data.obj);
        break;
      case "Judge":
        this.generalDataService.processJudgeUpdate(data.obj);
        break;
      case "DeleteJudge":
        this.generalDataService.processJudgeDelete(data.obj);
        break;
      case "Link":
        this.generalDataService.processLinkUpdate(data.obj);
        break;
      case "DeleteLink":
        this.generalDataService.processLinkDelete(data.obj);
        break;
      case "Portfolio":
        this.portfolioService.processPortfolioUpdate(data.obj);
        break;
      case "PortfolioDelete":
        this.portfolioService.removePortfolioFromCache(data.obj.id);
        break;
      case "Client":
        this.authService.getClientById(data.obj.clientId, true).pipe(take(1)).subscribe();
        break;
      case "ClientDelete":
        this.authService.removeClientFromCache(data.obj.clientId);
        break;
      case "Vendor":
        this.authService.getVendorById(data.obj.vendorId, true).pipe(take(1)).subscribe();
        break;
      case "VendorDelete":
        this.authService.removeVendorFromCache(data.obj.vendorId);
        break;
      case "LawFirm":
        this.authService.getLawFirmById(data.obj.lawFirmId, true).pipe(take(1)).subscribe();
        break;
      case "LawFirmDelete":
        this.authService.removeLawFirmFromCache(data.obj.lawFirmId);
        break;
      default:
        break;
    }
  }

  private processHubReconnection(){
    this.propertyService.updatePropertyData().pipe(take(1)).subscribe();
  }

  //#region property handling
  get properties(){
    return this.propertyService.getProperties();
  }

  get soldProperties(){
    return this.propertyService.getSoldProperties();
  }

  public propertyOffers(){
    return this.propertyService.getPropertyOffers();
  }

  public propertyOrders(){
    return this.propertyService.getPropertyOrders();
  }
  //#endregion

  //#region state handling
  get states(){
    return this.generalDataService.getStates();
  }

  public getStateByCode(code:string){
    return this.states.pipe(map(sa=>sa.find(s=>s.stateCode==code && s.countryCode=='US')));
  }
  //#endregion

  //#region county handling
  public getCounties(stateId:number){
    return this.generalDataService.getCounties(stateId);
  }

  public getCountyByName(stateId: number, countyName:string){
    return this.generalDataService.getCountyByName(stateId,countyName);
  }
  //#endregion

  //#region law firms
  get lawFirms(){
    return this.authService.getLawFirms();
  }

  public getLawFirmById(lawFirmId:number){
    return this.authService.getLawFirmById(lawFirmId);
  }
  //#endregion

  //#region clients
  get clients(){
    return this.authService.getClients();
  }

  public getClientById(clientId:number){
    return this.authService.getClientById(clientId);
  }
  //#endregion

  //#region auction rules
  public getAuctionRule(saleTypeId:number, stateId:number, countyId:number){
    return this.generalDataService.getAuctionRulesBySaleType(saleTypeId).pipe(take(1), map(rules=>{
      let result=undefined;
      if(countyId>0){
        //check for county specific rule
        result = rules.find(r=>r.entityType=="c" && r.entityId==countyId);
      }
      if(!result){
        //check for state specific rule if no rule for county found
        result = rules.find(r=>r.entityType=="s" && r.entityId==stateId);
      }
      if(!result){
        //check for global rule if either no rule for state found
        result = rules.find(r=>r.entityType=="g");
      }
      return result;
    }));
  }
  //#endregion

  //#region legal ad rules
  public getLegalAdRule(saleTypeId:number, auctionType:number, stateId:number, countyId:number){
    return this.generalDataService.getLegalAdRulesBySaleType(saleTypeId).pipe(take(1), map(rules=>{
      let result=undefined;
      rules = rules.filter(r=>r.auctionType==auctionType); //filter for auction type
      if(countyId>0){
        //check for county specific rule
        result = rules.find(r=>r.entityType=="c" && r.entityId==countyId);
      }
      if(!result){
        //check for state specific rule if no rule for county found
        result = rules.find(r=>r.entityType=="s" && r.entityId==stateId);
      }
      if(!result){
        //check for global rule if no rule for state found
        result = rules.find(r=>r.entityType=="g");
      }
      return result;
    }));
  }
  //#endregion

  //#region legal status handling
  get saleTypes(){
    return this.propertyService.getSaleTypes();
  }

  public getSaleTypeById(id:number){
    return this.propertyService.getSaleTypeById(id);
  }
  //#endregion

  //#region document types handling
  get documentTypes(){
    return this.generalDataService.getDocumentTypes();
  }

  public getDocumentTypeById(id:number){
    return this.generalDataService.getDocumentTypeById(id);
  }
  //#endregion

  //#region promoted properties
  get featuredProperties(): Observable<Property[]>{
    return this.properties.pipe(map(pa=>pa.filter(p=>p.auction?.showFeatured)));
  } 

  get comingSoonProperties(): Observable<Property[]>{
    return this.properties.pipe(map(pa=>pa.filter(p=>p.auction?.showComingSoon)));
  }

  get auctionEndingSoonProperties(): Observable<Property[]>{
    //let now=new Date().getTime();
    //let diff=5*24*60*60*1000; //5 days difference in milliseconds 
    return this.properties.pipe(map(pa=>pa.filter(p=> (p.auction?.auctionType===1 || p.auction?.auctionType===4) && !p.auction?.auctionEnded && (p.auction?.auctionIsLive || p.auction?.comingSoon))));
  }

  get recentlyAddedProperties(): Observable<Property[]>{
    let now=new Date().getTime();
    let diff=10*24*60*60*1000; //1 days difference in milliseconds
    //Show all auctions that were set to Coming Soon or went Live in the last 10 days unless none meet the criteria, then show the most recent 4 regardless. Fill with properties outside the time criteria to ensure 4 always show. Link “Recently Added” text to “active auctions” page sorted by “newest”.
    return this.properties.pipe(map(pa=>{
      let result = pa.filter(p=>!p.auction?.auctionEnded && p.visibilitySince && (now-p.visibilitySince?.getTime())<=diff);
      let remainder = pa.filter(p=> !p.auction?.auctionEnded && !result.includes(p));
      if(result.length<4) result = remainder.sort((a,b)=>b.id-a.id).slice(0,4-result.length);
      return result;
    }));
  } 
  //#endregion

  //#region favorites
  get favoriteProperties(){
    return this.propertyService.getFavoriteProperties();
  }

  public addToFavorites(p:Property){
    return this.propertyService.addToFavorites(p);
  }

  public removeFromFavorites(p:Property){
    return this.propertyService.removeFromFavorites(p);
  }

  public removeAllFavorites(){
    return this.propertyService.removeAllFavorites();
  }

  public isFavorite(propertyId:number):boolean{
    return this.propertyService.isFavorite(propertyId);
  }
  //#endregion

  //#region compare
  get compareProperties(){
    return this.propertyService.getCompareProperties();
  }

  public addToComparables(p:Property){
    return this.propertyService.addToComparables(p);
  }

  public removeFromComparables(p:Property){
    return this.propertyService.removeFromComparables(p);
  }

  public removeAllComparables(){
    return this.propertyService.removeAllComparables();
  }

  public isComparable(propertyId:number):boolean{
    return this.propertyService.isComparable(propertyId);
  }
  //#endregion

  //#region bidding
  public getAuctionBidHistory(p:Property){
    return this.auctionService.getAuctionBidHistory(p);
  }
  //#endregion

  public getAddress(lat: number, lng: number){
    return this.propertyService.getAddress(lat,lng);
  }

  public getLocation(address:string){
    return this.propertyService.getLocation(address);
  }

  public getStructuredAddress(address:string){
    return this.propertyService.getStructuredAddress(address);
  }

  public filterPropertyData(properties: Property[], params: any, listingType: any, sort?:any) {

    // --- listingType predicate, built once ---
    let listingPredicate: (p: Property) => boolean;
    switch (listingType) {
      case 'available':
        listingPredicate = p => p.auction!=undefined && (p.auction.auctionIsLiveOrCancelledWithinSchedule || (p.auction.comingSoon && !p.auction.auctionEnded));
        break;
      case 'auction':
        listingPredicate = p => p.auction!=undefined && p.auction.auctionType === 1 && (p.auction.auctionIsLiveOrCancelledWithinSchedule || (p.auction.comingSoon && !p.auction.auctionEnded));
        break;
      case 'mobn':
        listingPredicate = p => p.auction!=undefined && p.auction.auctionType === 2 && (p.auction.auctionIsLive || (p.auction.comingSoon && !p.auction.auctionEnded));
        break;
      case 'tl':
        listingPredicate = p => p.auction!=undefined && p.auction.auctionType === 3 && (p.auction.auctionIsLive || (p.auction.comingSoon && !p.auction.auctionEnded));
        break;
      case 'cfo':
        listingPredicate = p => p.auction!=undefined && p.auction.auctionType === 4 && (p.auction.auctionIsLiveOrCancelledWithinSchedule || (p.auction.comingSoon && !p.auction.auctionEnded));
        break;
      case 'sold':
        listingPredicate = p => (p.auction?.auctionStatus === 6 || p.sold) && p.showSold;
        break;
      default:
        listingPredicate = p => p.auction!=undefined && (p.auction.auctionIsLive || (p.auction.comingSoon && !(p.auction.auctionEnded || p.auction.auctionIsCancelled)));
        break;
    }

    // --- lookup structures, built once ---
    const auctionTypeSet = params?.auctionTypes ? new Set<number>((params.auctionTypes as AuctionType[]).map(at => at.id)) : null;
    const ptMap = this.buildPropertyTypeMap(params?.propertyTypes, pt => pt.subTypeList?.map(pst => pst.id));
    const stateSet = params?.states ? new Set<string>((params.states as State[]).map(s => s.stateCode)) : null;
    const saleTypeSet = params?.saleTypes ? new Set<number>((params.saleTypes as SaleType[]).map(st => st.id)) : null;
    const pf: PropertyFeatures | null = params?.propertyFeatures ?? null;
    const propIdSet = params?.propertySearch?.propertyIds ? new Set<number>(params.propertySearch.propertyIds) : null;

    const filtered = properties.filter(property => {
      if (!listingPredicate(property)) return false;

      if (auctionTypeSet && !auctionTypeSet.has(property.auction?.auctionType as number)) return false;

      if (ptMap && !this.matchesPropertyType(ptMap, property.type?.id, property.subType?.id)) return false;

      if (property.address?.state && stateSet && !stateSet.has(property.address.state)) return false;

      if (saleTypeSet && !saleTypeSet.has(property.saleType?.id as number)) return false;

      if (pf) {
        if (pf.beds && property.bedrooms && property.bedrooms < pf.beds) return false;
        if (pf.baths && property.totalBathRooms && property.totalBathRooms < pf.baths) return false;
        if (pf.garages && property.garages && property.garages < pf.garages) return false;
        if (pf.areaFrom && property.area && property.area < pf.areaFrom) return false;
        if (pf.areaTo && property.area && property.area > pf.areaTo) return false;
      }

      if (propIdSet && !propIdSet.has(property.id)) return false;

      return true;
    });

    return this.sortPropertyData(sort, filtered);
  }

  public filterSoldPropertyData(properties: SoldProperty[], params: any, sort?: any) {
    if (!params) return this.sortSoldPropertyData(sort, properties);

    const ptMap = this.buildPropertyTypeMap(params.propertyTypes, pt => pt.subTypeList?.map(pst => pst.id));
    const stateSet = params.states ? new Set<string>((params.states as State[]).map(s => s.stateCode)) : null;
    const saleTypeSet = params.saleTypes ? new Set<number>((params.saleTypes as SaleType[]).map(st => st.id)) : null;
    const pf: PropertyFeatures | null = params.propertyFeatures ?? null;
    const propIdSet = params.propertySearch?.propertyIds ? new Set<number>(params.propertySearch.propertyIds) : null;

    const filtered = properties.filter(property => {
      if (ptMap && !this.matchesPropertyType(ptMap, property.type?.id, property.subType?.id)) return false;

      if (property.address?.state &&stateSet && !stateSet.has(property.address.state)) return false;

      if (saleTypeSet && !saleTypeSet.has(property.saleType?.id as number)) return false;

      if (pf) {
        if (pf.beds && property.bedrooms && property.bedrooms < pf.beds) return false;
        if (pf.baths && property.totalBathRooms && property.totalBathRooms < pf.baths) return false;
        if (pf.garages && property.garages && property.garages < pf.garages) return false;
        if (pf.areaFrom && property.area && property.area < pf.areaFrom) return false;
        if (pf.areaTo && property.area && property.area > pf.areaTo) return false;
      }

      if (propIdSet && !propIdSet.has(property.id)) return false;

      return true;
    });

    return this.sortSoldPropertyData(sort, filtered);
  }

  public filterPropertyAbstractData(propertyAbstracts: PropertyAbstract[], params: any, listingType: any, sort?: any) {
    // --- build the listingType predicate once ---
    let listingPredicate: (p: PropertyAbstract) => boolean = () => true;
    if (listingType) {
      switch (listingType) {
        case 'available':
          listingPredicate = p => p.auctionIsLiveOrCancelledWithinSchedule || (p.comingSoon && !p.auctionEnded);
          break;
        case 'auction':
          listingPredicate = p => p.auctionType === 1 && (p.auctionIsLiveOrCancelledWithinSchedule || (p.comingSoon && !p.auctionEnded));
          break;
        case 'mobn':
          listingPredicate = p => p.auctionType === 2 && (p.isLive || (p.comingSoon && !p.auctionEnded));
          break;
        case 'cfo':
          listingPredicate = p => p.auctionType === 3 && (p.isLive || (p.comingSoon && !p.auctionEnded));
          break;
        case 'tl':
          listingPredicate = p => p.auctionType === 4 && (p.auctionIsLiveOrCancelledWithinSchedule || (p.comingSoon && !p.auctionEnded));
          break;
        case 'sold':
          listingPredicate = p => p.auctionStatus === 6;
          break;
        default:
          listingPredicate = p => p.isLive || (p.comingSoon && !(p.auctionEnded || p.isCancelled));
          break;
      }
    }

    // --- build Set/Map lookups once instead of arrays scanned per-property ---
    const clientSet = params?.clients ? new Set<number>(params.clients) : null;
    const includeNoClient = !!params?.noClientAssigned;
    const clientFilterActive = !!clientSet || includeNoClient;
    const amSet = params?.assetManagers ? new Set<string>(params.assetManagers) : null;
    const attySet = params?.attorneys ? new Set<string>(params.attorneys) : null;
    const pmSet = params?.propertyManagers ? new Set<string>(params.propertyManagers) : null;
    const judgeSet = params?.judges ? new Set<number>(params.judges) : null;
    const stateSet = params?.states ? new Set<string>((params.states as State[]).map(s => s.stateCode)) : null;
    const saleTypeSet = params?.saleTypes ? new Set<number>((params.saleTypes as SaleType[]).map(st => st.id)) : null;
    const phaseSet = params?.propertyPhases ? new Set<number>(params.propertyPhases) : null;
    const propIdSet = params?.propertySearch?.propertyIds ? new Set<number>(params.propertySearch.propertyIds) : null;

    // propertyTypes needs id -> subtype set, since subtype logic is conditional
    const ptMap = this.buildPropertyTypeMap(params?.propertyTypes, pt => pt.subTypeIdList);

    const filtered = propertyAbstracts.filter(property => {
      if (!listingPredicate(property)) return false;

      if (clientFilterActive) {
        const hasClient = property.clientId != null;
        const matches = hasClient ? !!clientSet?.has(property.clientId!) : includeNoClient;
        if (!matches) return false;
      }

      if (amSet && !(property.assetManagerIds?.some(id => amSet.has(id)))) return false;

      if (attySet && !(property.attorneyIds?.some(id => attySet.has(id)))) return false;

      if (pmSet && (!property.propertyManagerId || !pmSet.has(property.propertyManagerId))) return false;

      if (judgeSet && (!property.judgeId || !judgeSet.has(property.judgeId))) return false;

      if (stateSet && (!property.address?.state || !stateSet.has(property.address.state))) return false;

      if (saleTypeSet && (!property.saleType || !saleTypeSet.has(property.saleType))) return false;

      if (ptMap && !this.matchesPropertyType(ptMap, property.propertyType, property.propertySubType)) return false;

      if (phaseSet && !phaseSet.has(property.propertyPhase.id)) return false;

      if (propIdSet && !propIdSet.has(property.id)) return false;

      return true;
    });

    return this.sortPropertyAbstractData(sort, filtered);
  }

   /** Builds id -> Set<subTypeId> (or undefined if pt has no subTypeList) from a PropertyType[] param. */
  private buildPropertyTypeMap(propertyTypes: PropertyType[] | undefined, getSubTypeIds: (pt: PropertyType) => number[] | undefined): Map<number, Set<number> | undefined> | null {
    if (!propertyTypes) return null;
    const map = new Map<number, Set<number> | undefined>();
    for (const pt of propertyTypes) {
      const ids = getSubTypeIds(pt);
      map.set(pt.id, ids ? new Set(ids) : undefined);
    }
    return map;
  }

  private matchesPropertyType(ptMap: Map<number, Set<number> | undefined>, typeId: number | undefined, subTypeId: number | undefined): boolean {
    if (typeId === undefined || !ptMap.has(typeId)) return false;
    const subTypes = ptMap.get(typeId);
    if (subTypeId === undefined) return true; // matches original: property.subType===undefined => match
    return subTypes ? subTypes.has(subTypeId) : false;
  }

  public sortPropertyData(sort: string, properties: Property[]): Property[] {
    if (!sort) return properties;

    const sorted = [...properties];

    switch (sort) {
      case 'Newest':
        return sorted.sort((a, b) => this.comparePropertyVisibility(b, a));
      case 'Oldest':
        return sorted.sort((a, b) => this.comparePropertyVisibility(a, b));
      case 'Popular':
        return sorted.sort((a, b) => this.compareValues(b.views, a.views));
      case 'Price (Low to High)':
        return sorted.sort((a, b) => this.compareValues(a.auction?.currentPrice, b.auction?.currentPrice));
      case 'Price (High to Low)':
        return sorted.sort((a, b) => this.compareValues(b.auction?.currentPrice, a.auction?.currentPrice));
      case 'Ending Soonest':
      default:
        return sorted.sort((a, b) => this.compareAuctionEnding(a.auction, b.auction));
    }
  }

  public sortSoldPropertyData(sort: string, properties: SoldProperty[]): SoldProperty[] {
    if (!sort) return properties;

    const sorted = [...properties];

    switch (sort) {
      case 'Most Recent':
        return sorted.sort((a, b) => this.compareDates(b.soldAt, a.soldAt));
      case 'Price (Low to High)':
        return sorted.sort((a, b) => this.compareValues(a.soldFor, b.soldFor));
      case 'Price (High to Low)':
        return sorted.sort((a, b) => this.compareValues(b.soldFor, a.soldFor));
      default:
        return sorted.sort((a, b) => this.compareDates(a.soldAt, b.soldAt));
    }
  }

  public sortPropertyAbstractData(sort: string, propertyAbstracts: PropertyAbstract[]): PropertyAbstract[] {
    if (!sort) return propertyAbstracts;

    const sorted = [...propertyAbstracts];

    switch (sort) {
      case 'Newest':
        return sorted.sort((a, b) => this.comparePropertyAbstractVisibility(b, a));
      case 'Oldest':
        return sorted.sort((a, b) => this.comparePropertyAbstractVisibility(a, b));
      case 'Popular':
        return sorted.sort((a, b) => this.compareValues(b.views, a.views));
      case 'Price (Low to High)':
        return sorted.sort((a, b) => this.compareValues(a.currentBid, b.currentBid));
      case 'Price (High to Low)':
        return sorted.sort((a, b) => this.compareValues(b.currentBid, a.currentBid));
      case 'Ending Soonest':
      default:
        return sorted.sort((a, b) => this.comparePropertyAbstractAuctionEnding(a, b));
    }
  }

  public searchPropertyData(properties: Property[], searchString: string) {
    let result = new SearchResult();
    let si = new SearchItem("All Locations", "US");
    result.categories[2].items.push(si);

    if (!(searchString?.length > 0)) {
      return result;
    }

    const lcSearchString = searchString.toLowerCase();
    const state = this.stateArray.find(s => s.code.toLowerCase() === lcSearchString);

    if (state) {
      const stateSi = new SearchItem(state.code, "state");
      result.categories[0].items.push(stateSi);
      for (const p of properties) {
        if (p.address?.state === state.code) {
          stateSi.propertyIds.push(p.id);
        }
      }
      result.categories[3].items.push(new SearchItem(searchString, "text"));
      return result;
    }

    const matchingStates = this.stateArray.filter(s => s.name.toLowerCase().startsWith(lcSearchString) || s.code === lcSearchString);
    const matchingStateCodes = new Set(matchingStates.map(s => s.code));

    // group maps, keyed by "categoryIndex|resultType|label" -> SearchItem + Set for O(1) id dedupe
    const groupItems = new Map<string, { item: SearchItem; idSet: Set<number> }>();

    const getOrCreateGroup = (categoryIndex: number, resultType: string, label: string) => {
      const key = categoryIndex + "|" + resultType + "|" + label;
      let entry = groupItems.get(key);
      if (!entry) {
        const item = new SearchItem(label, resultType);
        entry = { item, idSet: new Set<number>() };
        groupItems.set(key, entry);
        result.categories[categoryIndex].items.push(item);
      }
      return entry;
    };

    for (const p of properties) {
      const addr = p.address;
      if (!addr) continue;

      if (addr.city?.toLowerCase().startsWith(lcSearchString)) {
        const { item, idSet } = getOrCreateGroup(0, "city", addr.city + ", " + addr.state);
        if (!idSet.has(p.id)) { idSet.add(p.id); item.propertyIds.push(p.id); }
      }

      if (addr.county?.toLowerCase().startsWith(lcSearchString)) {
        const { item, idSet } = getOrCreateGroup(0, "county", addr.county + ", " + addr.state);
        if (!idSet.has(p.id)) { idSet.add(p.id); item.propertyIds.push(p.id); }
      }

      const stateMatches = addr.state && matchingStateCodes.has(addr.state);

      if (stateMatches) {
        const { item, idSet } = getOrCreateGroup(0, "state", addr.state!);
        if (!idSet.has(p.id)) { idSet.add(p.id); item.propertyIds.push(p.id); }
      }

      if (lcSearchString.length > 1 && (p.searchData.indexOf(lcSearchString) > -1 || stateMatches)) {
        const { item, idSet } = getOrCreateGroup(1, "address", p.formattedAddress);
        if (!idSet.has(p.id)) { idSet.add(p.id); item.propertyIds.push(p.id); }
      }
    }

    result.categories[3].items.push(new SearchItem(searchString, "text"));
    return result;
  }

  private compareValues(valueA: number | undefined, valueB: number | undefined): number {
    const aExists = valueA != undefined;
    const bExists = valueB != undefined;
    if (aExists && bExists) return valueA - valueB;
    if (aExists) return -1;
    if (bExists) return 1;
    return 0;
  }

  private compareDates(dateA: Date|undefined, dateB: Date|undefined): number {
    if (dateA && dateB) return dateA.getTime() - dateB.getTime();
    if (dateA) return -1;
    if (dateB) return 1;
    return 0;
  }

  private compareAuctionEnding(auctionA:Auction|undefined, auctionB:Auction|undefined){
    if(auctionA?.auctionIsLive && auctionB?.auctionIsLive) {
      if(auctionA.auctionEndingUnix && auctionB.auctionEndingUnix) return auctionA.auctionEndingUnix - auctionB.auctionEndingUnix;
      if(auctionA?.auctionEnding) return -1
      if(auctionB?.auctionEnding) return 1;
      return 0;
    }
    if(auctionA?.auctionIsLive) return -1;
    if(auctionB?.auctionIsLive) return 1;
    //both not live => check based on endingDate
    if(auctionA?.auctionEndingUnix && auctionB?.auctionEndingUnix) return auctionA.auctionEndingUnix - auctionB.auctionEndingUnix;
    if(auctionA?.auctionEnding) return -1
    if(auctionB?.auctionEnding) return 1;
    return 0;
  }

  private comparePropertyAbstractAuctionEnding(paA: PropertyAbstract, paB: PropertyAbstract): number {
    const hasEnding = (pa: PropertyAbstract) => pa.auctionTypeHasSchedule && pa.auctionEnding;

    if (paA?.isLive && paB?.isLive) {
      if (hasEnding(paA) && hasEnding(paB)) return paA.auctionEnding!.getTime() - paB.auctionEnding!.getTime();
      if (hasEnding(paA)) return -1;
      if (hasEnding(paB)) return 1;
      return 0;
    }

    if (paA?.isLive) return -1;
    if (paB?.isLive) return 1;

    // both not live — oldest ending last
    if (hasEnding(paA) && hasEnding(paB)) return paB.auctionEnding!.getTime() - paA.auctionEnding!.getTime();
    if (hasEnding(paA)) return -1;
    if (hasEnding(paB)) return 1;
    return 0;
  }

  private comparePropertyVisibility(propertyA: Property, propertyB: Property): number {
    if (propertyA?.visibilitySince && propertyB?.visibilitySince) 
      return propertyA.visibilitySince.getTime() - propertyB.visibilitySince.getTime();
    if (propertyA?.visibilitySince) return -1;
    if (propertyB?.visibilitySince) return 1;
    return 0;
  }

  private comparePropertyAbstractVisibility(propertyA: PropertyAbstract, propertyB: PropertyAbstract): number {
    if (propertyA?.visibilitySince && propertyB?.visibilitySince) 
      return propertyA.visibilitySince.getTime() - propertyB.visibilitySince.getTime();
    if (propertyA?.visibilitySince) return -1;
    if (propertyB?.visibilitySince) return 1;
    return 0;
  }

  public paginator<T extends unknown[]>(items: T, page?: number, perPage?: number) {
    page = page || 1;
    perPage = perPage || 4;
    const offset = (page - 1) * perPage;

    const paginatedItems = items.slice(
      offset,
      offset + perPage
    ) as T;

    const totalPages = Math.ceil(items.length / perPage);

    return {
      data: paginatedItems,
      pagination: {
        page: page,
        perPage: perPage,
        prePage: page > 1 ? page - 1 : undefined,
        nextPage: totalPages > page ? page + 1 : undefined,
        total: items.length,
        totalPages: totalPages || undefined
      }
    };
  }

  public filterPropertyOfferData(data:any[], params: any, sort?: any, page?: number, perPage?: number){ 
    if(params){
      if(params.address){
        data = data.filter(property => property.formattedAddress.toUpperCase().indexOf(params.address.toUpperCase(), 0)>=0);
      }
    }

    this.sortPropertyOfferData(sort, data);
    return this.paginator(data, page, perPage)
  }

  public sortPropertyOfferData(sort: any, data: any[]){
    if(sort){
      switch (sort) {
        case 'Ending Soonest':
          data = data.sort((a,b) => {
            if(a.auctionEnded==b.auctionEnded){
              if(a.auctionEndingUnix > b.auctionEndingUnix){
                return 1;
              }
              if(a.auctionEndingUnix < b.auctionEndingUnix){
                return -1;
              }
            }
            else if(a.auctionEnded==true){
              return 1
            }
            else{
              return -1
            }
            return 0;  
          });
        break;
        default:
          break;
      }
    }
    return data;
  }

  public filterPropertyOrderData(data: any[], params: any, sort?: any, page?: number, perPage?: number){ 
    if(params){
      if(params.address){
        data = data.filter(property => property.formattedAddress.toUpperCase().indexOf(params.address.toUpperCase(), 0)>=0);
      }
    }

    this.sortPropertyOrderData(sort, data);
    return this.paginator(data, page, perPage)
  }

  public sortPropertyOrderData(sort: any, data: any[]){
    if(sort){
      switch (sort) {
        case 'Most Recent':
          data = data.sort((a, b)=> {return b.soldAtUnix - a.soldAtUnix});           
          break;
        default:
          break;
      }
    }
    return data;
  }

  public filterBuyerRepresentationData(data: any[], params: any, sort?: any, page?: number, perPage?: number){ 
    if(params){
      if(params.address){
        data = data.filter(property => property.formattedAddress.toUpperCase().indexOf(params.address.toUpperCase(), 0)>=0);
      }
    }

    this.sortBuyerRepresentationData(sort, data);
    return this.paginator(data, page, perPage)
  }

  public sortBuyerRepresentationData(sort: any, data: any[]){
    if(sort){
      switch (sort) {
        case 'Buyer Name':
          data = data.sort((a, b)=> {
            if(a.buyerFullName > b.buyerFullName){
              return 1;
            }
            else if(a.buyerFullName < b.buyerFullName){
              return -1;
            }
            else return 0;
          });           
          break;
        default:
          break;
      }
    }
    return data;
  }

  get stateCodes(){
    return [ 'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY' ];
  }
  get stateArray(){
    return [
      {
        "name": "Alabama",
        "code": "AL"
      },
      {
        "name": "Alaska",
        "code": "AK"
      },
      {
        "name": "American Samoa",
        "code": "AS"
      },
      {
        "name": "Arizona",
        "code": "AZ"
      },
      {
        "name": "Arkansas",
        "code": "AR"
      },
      {
        "name": "California",
        "code": "CA"
      },
      {
        "name": "Colorado",
        "code": "CO"
      },
      {
        "name": "Connecticut",
        "code": "CT"
      },
      {
        "name": "Delaware",
        "code": "DE"
      },
      {
        "name": "District Of Columbia",
        "code": "DC"
      },
      {
        "name": "Federated States Of Micronesia",
        "code": "FM"
      },
      {
        "name": "Florida",
        "code": "FL"
      },
      {
        "name": "Georgia",
        "code": "GA"
      },
      {
        "name": "Guam",
        "code": "GU"
      },
      {
        "name": "Hawaii",
        "code": "HI"
      },
      {
        "name": "Idaho",
        "code": "ID"
      },
      {
        "name": "Illinois",
        "code": "IL"
      },
      {
        "name": "Indiana",
        "code": "IN"
      },
      {
        "name": "Iowa",
        "code": "IA"
      },
      {
        "name": "Kansas",
        "code": "KS"
      },
      {
        "name": "Kentucky",
        "code": "KY"
      },
      {
        "name": "Louisiana",
        "code": "LA"
      },
      {
        "name": "Maine",
        "code": "ME"
      },
      {
        "name": "Marshall Islands",
        "code": "MH"
      },
      {
        "name": "Maryland",
        "code": "MD"
      },
      {
        "name": "Massachusetts",
        "code": "MA"
      },
      {
        "name": "Michigan",
        "code": "MI"
      },
      {
        "name": "Minnesota",
        "code": "MN"
      },
      {
        "name": "Mississippi",
        "code": "MS"
      },
      {
        "name": "Missouri",
        "code": "MO"
      },
      {
        "name": "Montana",
        "code": "MT"
      },
      {
        "name": "Nebraska",
        "code": "NE"
      },
      {
        "name": "Nevada",
        "code": "NV"
      },
      {
        "name": "New Hampshire",
        "code": "NH"
      },
      {
        "name": "New Jersey",
        "code": "NJ"
      },
      {
        "name": "New Mexico",
        "code": "NM"
      },
      {
        "name": "New York",
        "code": "NY"
      },
      {
        "name": "North Carolina",
        "code": "NC"
      },
      {
        "name": "North Dakota",
        "code": "ND"
      },
      {
        "name": "Northern Mariana Islands",
        "code": "MP"
      },
      {
        "name": "Ohio",
        "code": "OH"
      },
      {
        "name": "Oklahoma",
        "code": "OK"
      },
      {
        "name": "Oregon",
        "code": "OR"
      },
      {
        "name": "Palau",
        "code": "PW"
      },
      {
        "name": "Pennsylvania",
        "code": "PA"
      },
      {
        "name": "Puerto Rico",
        "code": "PR"
      },
      {
        "name": "Rhode Island",
        "code": "RI"
      },
      {
        "name": "South Carolina",
        "code": "SC"
      },
      {
        "name": "South Dakota",
        "code": "SD"
      },
      {
        "name": "Tennessee",
        "code": "TN"
      },
      {
        "name": "Texas",
        "code": "TX"
      },
      {
        "name": "Utah",
        "code": "UT"
      },
      {
        "name": "Vermont",
        "code": "VT"
      },
      {
        "name": "Virgin Islands",
        "code": "VI"
      },
      {
        "name": "Virginia",
        "code": "VA"
      },
      {
        "name": "Washington",
        "code": "WA"
      },
      {
        "name": "West Virginia",
        "code": "WV"
      },
      {
        "name": "Wisconsin",
        "code": "WI"
      },
      {
        "name": "Wyoming",
        "code": "WY"
      }
    ]
  }
}
